import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '@/shared/components/ui/icons';
import { WEBSITE_ANALYTICS_DEFAULT_DAYS } from '../constants';
import { useWebsiteAnalyticsConversion } from '../hooks/useWebsiteAnalyticsConversion';
import { useWebsiteAnalyticsDevices } from '../hooks/useWebsiteAnalyticsDevices';
import { useWebsiteAnalyticsProducts } from '../hooks/useWebsiteAnalyticsProducts';
import { useWebsiteAnalyticsSources } from '../hooks/useWebsiteAnalyticsSources';
import { useWebsiteAnalyticsTopPages } from '../hooks/useWebsiteAnalyticsTopPages';
import { useWebsiteAnalyticsTrends } from '../hooks/useWebsiteAnalyticsTrends';
import type { WebsiteAnalyticsDays } from '../types';
import { ProductAnalyticsFilters } from './ProductAnalyticsFilters';
import {
  ProductAnalyticsConversionPanel,
  ProductAnalyticsProductCards,
} from './ProductAnalyticsProductCards';
import {
  ProductAnalyticsDevicesPanel,
  ProductAnalyticsSourcesPanel,
  ProductAnalyticsTopPagesPanel,
  ProductAnalyticsTrendsPanel,
} from './ProductAnalyticsDataPanels';

export function ProductAnalyticsSection() {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState<WebsiteAnalyticsDays>(WEBSITE_ANALYTICS_DEFAULT_DAYS);
  const [productSlug, setProductSlug] = useState('');

  const filterSlug = productSlug || undefined;
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  const productsQuery = useWebsiteAnalyticsProducts(days, true);
  const trendsQuery = useWebsiteAnalyticsTrends(days, filterSlug, true);
  const topPagesQuery = useWebsiteAnalyticsTopPages(days, filterSlug, true);
  const sourcesQuery = useWebsiteAnalyticsSources(days, filterSlug, true);
  const devicesQuery = useWebsiteAnalyticsDevices(days, filterSlug, true);
  const conversionQuery = useWebsiteAnalyticsConversion(days, filterSlug, true);

  const productOptions = useMemo(() => {
    const slugs = productsQuery.data?.items.map((item) => item.productSlug) ?? [];
    return [...new Set(slugs)].sort((a, b) => a.localeCompare(b));
  }, [productsQuery.data?.items]);

  const isFetching =
    productsQuery.isFetching ||
    trendsQuery.isFetching ||
    topPagesQuery.isFetching ||
    sourcesQuery.isFetching ||
    devicesQuery.isFetching ||
    conversionQuery.isFetching;

  return (
    <div className="space-y-6 border-t border-border pt-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            {t('websiteActivity.products.title')}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('websiteActivity.products.subtitle')}
          </p>
        </div>
        {isFetching ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icons.RefreshCw className="h-3 w-3 animate-spin" aria-hidden />
            {t('common.refresh')}
          </span>
        ) : null}
      </div>

      <ProductAnalyticsFilters
        days={days}
        productSlug={productSlug}
        productOptions={productOptions}
        onDaysChange={setDays}
        onProductChange={setProductSlug}
      />

      <ProductAnalyticsProductCards
        items={productsQuery.data?.items ?? []}
        selectedProductSlug={productSlug}
        isLoading={productsQuery.isLoading}
        isError={productsQuery.isError}
      />

      <ProductAnalyticsConversionPanel
        visitors={conversionQuery.data?.visitors ?? 0}
        registrations={conversionQuery.data?.registrations ?? 0}
        freeTrials={conversionQuery.data?.freeTrials ?? 0}
        payments={conversionQuery.data?.payments ?? 0}
        visitToRegistrationRate={conversionQuery.data?.visitToRegistrationRate ?? 0}
        registrationToTrialRate={conversionQuery.data?.registrationToTrialRate ?? 0}
        trialToPaymentRate={conversionQuery.data?.trialToPaymentRate ?? 0}
        isLoading={conversionQuery.isLoading}
        isError={conversionQuery.isError}
      />

      <ProductAnalyticsTrendsPanel
        items={trendsQuery.data?.items ?? []}
        locale={locale}
        isLoading={trendsQuery.isLoading}
        isError={trendsQuery.isError}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ProductAnalyticsTopPagesPanel
          items={topPagesQuery.data?.items ?? []}
          isLoading={topPagesQuery.isLoading}
          isError={topPagesQuery.isError}
        />
        <ProductAnalyticsSourcesPanel
          items={sourcesQuery.data?.items ?? []}
          isLoading={sourcesQuery.isLoading}
          isError={sourcesQuery.isError}
        />
      </div>

      <ProductAnalyticsDevicesPanel
        items={devicesQuery.data?.items ?? []}
        isLoading={devicesQuery.isLoading}
        isError={devicesQuery.isError}
      />
    </div>
  );
}
